import type {
  ApolloClientOptions,
  DefaultContext,
  NormalizedCacheObject,
  Operation,
} from '@apollo/client';
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { RetryLink } from '@apollo/client/link/retry';
import { relayStylePagination } from '@apollo/client/utilities';
import type { GraphQLError } from '@saleor/auth-sdk';
import { createSaleorAuthClient } from '@saleor/auth-sdk';
import fetch from 'cross-fetch';
import { sha256 } from 'crypto-hash';
import type { DocumentNode } from 'graphql';
import { print, stripIgnoredCharacters } from 'graphql';
import localforage from 'localforage';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';

import { getBrandConfig } from '@/lib/configuration/configuration';
// import { LRUCache } from 'lru-cache';
import {
  API_URI,
  CONFIGURATION_APP_API_URI,
  CONTENTFUL_ACCESS_TOKEN,
  CONTENTFUL_URI,
  IS_BROWSER,
  ORDER_APP_API_URI,
  POS_MANAGER_APP_API_URI,
  SALEOR_APP_TOKEN,
  SSR_MODE,
} from '@/lib/const';
import { captureError } from '@/lib/errors';
import type { AppErrorEvent } from '@/lib/types';
import { checkoutActions } from '@/states/checkout';
import { userActions } from '@/states/user';

import fragmentMatcher from './saleor/fragmentMatcher.schema.json';

export const APOLLO_CLIENTS = {
  orderApp: 'ORDERS_APP_CLIENT',
  contentful: 'CONTENTFUL_CLIENT',
  posManagerApp: 'POS_MANAGER_CLIENT',
  configurationApp: 'CONFIGURATION_CLIENT',
} as const;

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

const saleorFetch = (
  input: RequestInfo | URL,
  init?: RequestInit | undefined,
  additionalParams?: {
    allowPassingTokenToThirdPartyDomains?: boolean;
    withAuth?: boolean;
  },
): Promise<Response> => {
  //TODO it will throw error(if return {} ,will throw error) in dev mode, but prod not,
  //  if we can fix it, so much the better.
  if (getBrandConfig().disableSaleorApiCall) {
    const blob = new Blob([JSON.stringify({ data: {} }, null, 2)], {
      type: 'application/json',
    });
    const response = new Response(blob, {
      status: 200,
      statusText: '',
    });

    return Promise.resolve(response);
  }
  if (additionalParams?.withAuth) {
    return saleorAuthClient.fetchWithAuth(input, init, additionalParams);
  }
  return fetch(input, init);
};

// TODO: Can we use this in auth link?
// https://github.com/nikitowsky/next-advanced-apollo-starter/blob/master/src/lib/apollo.tsx#L16

export const saleorAuthClient = createSaleorAuthClient({
  saleorApiUrl: API_URI,
  onAuthRefresh: (isAuthenticating) => {
    if (isAuthenticating) {
      userActions.updateProcessingState({ initializing: true });
    }
    userActions.setAuthenticated({ isAuthenticating });
  },
});

const saleorLink = (secure: boolean) =>
  secure
    ? createHttpLink({
        headers: { Authorization: `Bearer ${SALEOR_APP_TOKEN}` },
        uri: API_URI,
        fetch: saleorFetch,
      })
    : createHttpLink({
        uri: API_URI,
        fetch: (input, init) => {
          return saleorFetch(input, init, { withAuth: IS_BROWSER });
        },
      });
const orderAppLink = (secure: boolean) =>
  secure
    ? createHttpLink({
        headers: { Authorization: `Bearer ${SALEOR_APP_TOKEN}` },
        uri: ORDER_APP_API_URI,
        fetch: saleorFetch,
      })
    : createHttpLink({
        uri: ORDER_APP_API_URI,
        fetch: (input, init) => {
          return saleorFetch(input, init, {
            withAuth: IS_BROWSER,
            allowPassingTokenToThirdPartyDomains: true,
          });
        },
      });

const posManagerAppLink = (secure: boolean) =>
  secure
    ? createHttpLink({
        headers: { Authorization: `Bearer ${SALEOR_APP_TOKEN}` },
        uri: POS_MANAGER_APP_API_URI,
        fetch: saleorFetch,
      })
    : createHttpLink({
        uri: POS_MANAGER_APP_API_URI,
        fetch: (input, init) => {
          return saleorFetch(input, init, {
            withAuth: IS_BROWSER,
            allowPassingTokenToThirdPartyDomains: true,
          });
        },
      });

const configurationAppLink = (secure: boolean) =>
  secure
    ? createHttpLink({
        headers: { Authorization: `Bearer ${SALEOR_APP_TOKEN}` },
        uri: CONFIGURATION_APP_API_URI,
        fetch: saleorFetch,
      })
    : createHttpLink({
        uri: CONFIGURATION_APP_API_URI,
        fetch: (input, init) => {
          return saleorFetch(input, init, {
            withAuth: IS_BROWSER,
            allowPassingTokenToThirdPartyDomains: true,
          });
        },
      });

const persistedQueriesLink = createPersistedQueryLink({
  generateHash: (query: DocumentNode) =>
    Promise.resolve<string>(sha256(stripIgnoredCharacters(print(query)))),
  useGETForHashedQueries: true,
});

// async function createPersistedQuery(
//   query: string,
//   hash: string,
//   variables = {},
// ) {
//   const url = `https://graphql.contentful.com/content/v1/spaces/${process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/${process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT}`;

//   const body = {
//     query: query,
//     variables: variables,
//     extensions: {
//       persistedQuery: {
//         sha256Hash: hash,
//         version: 1,
//       },
//     },
//   };

//   const response = await fetch(url, {
//     method: 'POST',
//     headers: {
//       Authorization: `Bearer ${process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN}`,
//       'Content-Type': 'application/json',
//     },
//     body: JSON.stringify(body),
//   });

//   const responseData = await response.json();

//   if (!response.ok) {
//     console.error('Error:', JSON.stringify(responseData, null, 2));
//     return false;
//   }

//   return true;
// }

// let memo: any;

// const isBrowser = typeof window !== 'undefined';
// const MAX_CACHE_SIZE = 1000;

// if (isBrowser) {
//   memo = {
//     get: (key: string) => {
//       const item = localStorage.getItem(key);
//       if (item) {
//         const accessOrder = JSON.parse(
//           localStorage.getItem('accessOrder') || '[]',
//         );
//         const index = accessOrder.indexOf(key);
//         if (index > -1) {
//           accessOrder.splice(index, 1);
//         }
//         accessOrder.push(key);
//         localStorage.setItem('accessOrder', JSON.stringify(accessOrder));
//         return JSON.parse(item);
//       }
//       return null;
//     },
//     set: (key: string, value: any) => {
//       localStorage.setItem(key, JSON.stringify(value));

//       const accessOrder = JSON.parse(
//         localStorage.getItem('accessOrder') || '[]',
//       );
//       const index = accessOrder.indexOf(key);
//       if (index > -1) {
//         accessOrder.splice(index, 1);
//       }
//       accessOrder.push(key);
//       localStorage.setItem('accessOrder', JSON.stringify(accessOrder));

//       if (accessOrder.length > MAX_CACHE_SIZE) {
//         const oldestKey = accessOrder.shift();
//         if (oldestKey) {
//           localStorage.removeItem(oldestKey);
//         }
//         localStorage.setItem('accessOrder', JSON.stringify(accessOrder));
//       }
//     },
//     has: (key: string) => {
//       return localStorage.getItem(key) !== null;
//     },
//   };
// } else {
//   memo = new LRUCache<string, any>({
//     max: 1000,
//     ttl: 1000 * 60 * 60 * 24,
//   });
// }

const contentfulLink = persistedQueriesLink.concat(
  createHttpLink({
    uri: CONTENTFUL_URI,
    headers: {
      Authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
    },
    fetch,
    print: (ast, originalPrint) => stripIgnoredCharacters(originalPrint(ast)),
  }),
);

const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 500,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error) => !!error,
  },
});

interface OperationContext extends DefaultContext {
  disableErrorNotification?: boolean;
}
export function CustomContext(context: OperationContext): OperationContext {
  return context;
}

interface GraphQLErrorException {
  code?: string;
}

interface ExtendedGraphQLError extends GraphQLError {
  extensions?: {
    exception?: GraphQLErrorException;
  };
}

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      const extendedError = error as unknown as ExtendedGraphQLError;
      captureError(new Error(`[GraphQL error]: ${error.message}`), {
        tags: { operation: operation.operationName },
        extra: {
          error: extendedError,
          operation,
        },
      });
    });

    const isSignatureExpiredError = graphQLErrors.some(
      (err) =>
        err.message === 'Signature has expired' ||
        err.message?.includes('Invalid token'),
    );
    const isCheckoutResolveError = graphQLErrors.some(
      (error) =>
        (error.message.startsWith("Couldn't resolve") ||
          error.message === 'Internal Server Error') &&
        error.path?.includes('checkout'),
    );

    // we get a 'PermissionDenied' error sometimes when the user's login has timed out,
    // meaning they are now an anonymous user, but the old checkoutId is still present in localforage,
    // this is because an anonymous user does not have access to the registered user's checkout object
    const isPermissionDeniedError = (
      graphQLErrors as unknown as ExtendedGraphQLError[]
    ).some((err) => err.extensions?.exception?.code === 'PermissionDenied');

    if (!SSR_MODE) {
      if (isSignatureExpiredError) {
        window.dispatchEvent(new Event('signatureExpired'));
      } else if (isCheckoutResolveError) {
        (async () => await checkoutActions.clear())();
      } else if (isPermissionDeniedError) {
        // if we try to remove the checkoutId key from localforage without a delay,
        // it will immediately be recreated with the original value, so setTimeout is used
        setTimeout(async () => {
          await localforage.removeItem('checkoutId');
        }, 200);
      } else {
        const { disableErrorNotification } =
          operation.getContext() as OperationContext;
        if (!disableErrorNotification) {
          const error = graphQLErrors[0];
          window.dispatchEvent(
            new CustomEvent<AppErrorEvent>('unhandledError', {
              detail: {
                error,
                errorInfo: error.message,
              },
            }),
          );
        }
      }
    }
  }

  // https://www.apollographql.com/docs/react/data/error-handling/#on-network-errors
  if (networkError) {
    captureError(new Error(`[Network error]: ${networkError.message}`), {
      tags: { operation: operation.operationName },
      extra: {
        error: networkError,
        operation,
      },
    });
  }
});

export type DynamicLinkClientName =
  (typeof APOLLO_CLIENTS)[keyof typeof APOLLO_CLIENTS];

type Link = ApolloLink;

type DynamicLink = { link: Link; name: DynamicLinkClientName };

const getLinkMap = (secure: boolean): DynamicLink[] => [
  { link: orderAppLink(secure), name: APOLLO_CLIENTS.orderApp },
  { link: contentfulLink, name: APOLLO_CLIENTS.contentful },
  { link: posManagerAppLink(secure), name: APOLLO_CLIENTS.posManagerApp },
  { link: configurationAppLink(secure), name: APOLLO_CLIENTS.configurationApp },
];

const isClientFromContext = (client: string) => (op: Operation) =>
  op.getContext().client === client;

const DynamicApolloLink = (secure: boolean) =>
  getLinkMap(secure).reduce<ApolloLink | undefined>((prevLink, nextLink) => {
    // When no name is specified, fallback to saleor client.
    if (!prevLink) {
      return ApolloLink.split(
        isClientFromContext(nextLink.name),
        nextLink.link,
        saleorLink(secure),
      );
    }
    return ApolloLink.split(
      isClientFromContext(nextLink.name),
      nextLink.link,
      prevLink,
    );
  }, undefined) as ApolloLink;

function createApolloClient({
  secure,
  ...opts
}: Partial<ApolloClientOptions<NormalizedCacheObject>> & {
  secure: boolean;
}) {
  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: fragmentMatcher.possibleTypes,
      typePolicies: {
        User: {
          fields: {
            orders: relayStylePagination(),
          },
        },
      },
    }),
    defaultOptions: {
      mutate: { errorPolicy: 'all' },
      query: { errorPolicy: 'all' },
    },
    link: from([retryLink, errorLink, DynamicApolloLink(secure)]),
    ssrMode: SSR_MODE, // set to true for SSR
    ...opts,
  });
}

export function initializeApollo(initialState = null, secure = false) {
  const _apolloClient = apolloClient ?? createApolloClient({ secure: secure });

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the initialState from getStaticProps/getServerSideProps in the existing cache
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: any[], sourceArray: any[]) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s) => !isEqual(d, s)),
        ),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }

  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return _apolloClient;
  }

  // Create the Apollo Client once in the client
  if (!apolloClient) {
    apolloClient = _apolloClient;
  }

  return _apolloClient;
}

/**
 * Apollo client meant to be used only in SSR and only for fetching
 * things that require permission.
 * Uses app token with certain admin permissions.
 */
export const initializeSecureApolloClient = () => {
  if (!SSR_MODE) {
    throw new Error('This client must be used only on the server side');
  }

  return createApolloClient({ secure: true });
};
